import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { api } from '../lib/api';
import { secureStorage } from '../lib/secureStorage';
import { AuthContextType, AuthUser, Clinic, User } from '../types';
import { safeConsole } from '../lib/piiProtection';
import { decodeJwtExpiryMs } from '../lib/jwtExpiry';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Refresh a bit before actual expiry, but never so soon (clock skew, decode edge cases) or so
// far out (a very long-lived token) that the timer becomes degenerate.
const REFRESH_BUFFER_MS = 2 * 60 * 1000;
const MIN_REFRESH_DELAY_MS = 30 * 1000;
const MAX_REFRESH_DELAY_MS = 6 * 60 * 60 * 1000;

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [userData, setUserData] = useState<User | null>(null);
  const [clinic, setClinic] = useState<Clinic | null>(null);
  const [loading, setLoading] = useState(true);
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  /**
   * Silently extends the session before the access token expires, so a still-active user is never
   * bounced to /login just because 24h passed. Every part of the app that reads the JWT does so
   * via secureStorage.getAuthToken() (WebSocket, raw fetch call sites, fetchApi) — authModule.refresh()
   * already updates that single source of truth, so scheduling it here is the only integration
   * point needed for the whole app to benefit, not just the main fetchApi path.
   */
  const scheduleTokenRefresh = useCallback((token: string) => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }

    const expiryMs = decodeJwtExpiryMs(token);
    if (expiryMs == null) return;

    const rawDelay = expiryMs - Date.now() - REFRESH_BUFFER_MS;
    const delay = Math.min(Math.max(rawDelay, MIN_REFRESH_DELAY_MS), MAX_REFRESH_DELAY_MS);

    refreshTimerRef.current = setTimeout(async () => {
      try {
        const response = await api.auth.refresh();
        if (response.error) return; // fetchApi's own 401 handling covers redirect-to-login
        const newToken = secureStorage.getAuthToken();
        if (newToken) scheduleTokenRefresh(newToken);
      } catch {
        // Refresh failed — let the existing 401-on-next-request handling take over.
      }
    }, delay);
  }, []);

  useEffect(() => {
    return () => {
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    };
  }, []);

  useEffect(() => {
    const initializeAuth = async () => {
      const token = secureStorage.getAuthToken();

      // If no token, immediately set loading to false
      if (!token) {
        secureStorage.removeAuthToken();
        setLoading(false);
        return;
      }

      // Check session validity only after confirming token exists
      if (!secureStorage.hasActiveSession()) {
        secureStorage.removeAuthToken();
        setLoading(false);
        return;
      }

      // Only make API call if we have a valid token
      try {
        const response = await api.auth.getCurrentUser();

        // Backend returns {data: {user, clinic}} which fetchApi wraps in {data: ...}
        const responseData = response.data?.data || response.data;
        
        if (responseData && responseData.user) {
          setUser({ id: responseData.user.id, email: responseData.user.name });
          setUserData(responseData.user);
          setClinic(responseData.clinic);
          scheduleTokenRefresh(token);
        } else {
          // Only remove token if this isn't a recent login (prevents removing freshly logged in token)
          if (!secureStorage.isRecentLogin()) {
            secureStorage.removeAuthToken();
          }
        }
      } catch (error) {
        safeConsole.error('Auth initialization error');
        // Only remove token if this isn't a recent login
        if (!secureStorage.isRecentLogin()) {
          secureStorage.removeAuthToken();
        }
      }

      setLoading(false);
    };

    initializeAuth();
  }, [scheduleTokenRefresh]);

  /** Shared by signIn and completeMfaLogin — both land here once a real session is established. */
  const applyLoginResponse = (response: { data?: any; error?: string }) => {
    const responseData = response.data?.data || response.data;
    const outerData = response.data as any;

    if (!responseData || !responseData.user) {
      safeConsole.error('Response data is missing user');
      throw new Error('Invalid response from server: missing user data');
    }

    // Reset session activity on login to prevent immediate logout
    sessionStorage.setItem('lastActivity', String(Date.now()));

    setUser({ id: responseData.user.id, email: responseData.user.name });
    setUserData(responseData.user);
    setClinic(responseData.clinic);

    // Multi-branch: store the default active clinic from login response.
    // TenantContext.tsx picks this up on mount.
    if (outerData?.activeClinicId) {
      secureStorage.setActiveClinicId(outerData.activeClinicId);
    }

    // authModule.login()/completeMfaLogin's underlying API call already stored the fresh token.
    const freshToken = secureStorage.getAuthToken();
    if (freshToken) scheduleTokenRefresh(freshToken);
  };

  const signIn = async (email: string, password: string) => {
    try {
      const response = await api.auth.login(email, password);

      if (response.error) {
        throw new Error(response.error);
      }

      const responseData = (response.data as any)?.data || response.data;

      // MFA-enabled account: password was correct, but no session exists yet — the caller
      // (Login.tsx) must collect a code and call completeMfaLogin with this token.
      if (responseData?.mfaRequired) {
        return { mfaRequired: true as const, mfaPendingToken: responseData.mfaPendingToken as string };
      }

      applyLoginResponse(response);
      return { mfaRequired: false as const };
    } catch (error) {
      safeConsole.error('Sign-in error');
      throw error;
    }
  };

  const completeMfaLogin = async (mfaPendingToken: string, code?: string, backupCode?: string) => {
    try {
      const response = await api.mfa.verify(mfaPendingToken, code, backupCode);
      if (response.error) {
        throw new Error(response.error);
      }
      applyLoginResponse(response);
    } catch (error) {
      safeConsole.error('MFA verification error');
      throw error;
    }
  };

  const signOut = async () => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
    try {
      // Clear session storage to prevent stale activity timestamps
      sessionStorage.removeItem('lastActivity');

      // Clear patient-linked localStorage to prevent PII leakage between sessions
      // (DPDP Act 2023 § 8: data must not be retained beyond session without consent)
      localStorage.removeItem('lastUsedPharmacyId');
      localStorage.removeItem('favoritePharmacies');
      localStorage.removeItem('favoriteDiagnoses');
      // recentPatients moved to sessionStorage (it holds patient names/IDs). Both are cleared:
      // sessionStorage is the live location, localStorage the legacy one still present on any
      // workstation that used an earlier build.
      sessionStorage.removeItem('recentPatients');
      localStorage.removeItem('recentPatients');

      // Clear state immediately to prevent race conditions with login
      setUser(null);
      setUserData(null);
      setClinic(null);
      secureStorage.removeAuthToken();

      // Call logout API non-blocking (don't wait for it or throw errors)
      // This prevents interference with login attempts right after auto-logout
      api.auth.logout().catch(() => {
        // Silently ignore logout API errors
        // Token is already cleared from storage, so logout is complete from client perspective
        safeConsole.log('Logout API call completed (error ignored)');
      });
    } catch (error) {
      safeConsole.error('Sign out error');
      // Still clear state and storage even if error occurs
      sessionStorage.removeItem('lastActivity');
      localStorage.removeItem('lastUsedPharmacyId');
      localStorage.removeItem('favoritePharmacies');
      localStorage.removeItem('favoriteDiagnoses');
      sessionStorage.removeItem('recentPatients');
      localStorage.removeItem('recentPatients');
      setUser(null);
      setUserData(null);
      setClinic(null);
      secureStorage.removeAuthToken();
    }
  };

  const refreshAuth = async () => {
    try {
      const response = await api.auth.getCurrentUser();
      
      // Backend returns {data: {user, clinic}} which fetchApi wraps in {data: ...}
      const responseData = response.data?.data || response.data;
      
      if (responseData && responseData.user) {
        setUser({ id: responseData.user.id, email: responseData.user.name });
        setUserData(responseData.user);
        setClinic(responseData.clinic);
      } else {
        throw new Error('Invalid response from server: missing user data');
      }
    } catch (error) {
      safeConsole.error('Refresh auth error');
      throw error;
    }
  };

  const value: AuthContextType = {
    user,
    userData,
    clinic,
    loading,
    isSuperAdmin: userData?.role === 'super_admin',
    signIn,
    completeMfaLogin,
    signOut,
    refreshAuth,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
